import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';
import {
  DASHBOARD_BRAND_GRADIENT,
  DASHBOARD_BRAND_PRIMARY,
  DASHBOARD_BRAND_PRIMARY_DARK,
  DASHBOARD_BRAND_PRIMARY_LIGHT,
} from './dashboardBrand';

const FONT_PRIMARY = '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const DEFAULT_SECONDARY = '#3366FF';
const DEFAULT_BACKGROUND = GREY[100];

function pxToRem(value: number) {
  return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': { fontSize: pxToRem(sm) },
    '@media (min-width:900px)': { fontSize: pxToRem(md) },
    '@media (min-width:1200px)': { fontSize: pxToRem(lg) },
  };
}

function resolvePrimaryShades(primaryMain: string) {
  const isDefaultBrand = primaryMain.toLowerCase() === DASHBOARD_BRAND_PRIMARY.toLowerCase();
  return {
    light: isDefaultBrand ? DASHBOARD_BRAND_PRIMARY_LIGHT : alpha(primaryMain, 0.72),
    dark: isDefaultBrand ? DASHBOARD_BRAND_PRIMARY_DARK : primaryMain,
  };
}

export type SherlookThemeOverrides = {
  primary?: string;
  secondary?: string;
  background?: string;
};

export function createSherlookTheme(overrides: SherlookThemeOverrides = {}) {
  const primaryMain = overrides.primary ?? DASHBOARD_BRAND_PRIMARY;
  const primaryShades = resolvePrimaryShades(primaryMain);
  const secondaryMain = overrides.secondary ?? DEFAULT_SECONDARY;
  const backgroundDefault = overrides.background ?? DEFAULT_BACKGROUND;

  const options: ThemeOptions = {
    palette: {
      primary: {
        light: primaryShades.light,
        main: primaryMain,
        dark: primaryShades.dark,
        contrastText: '#fff',
      },
      secondary: {
        light: '#84A9FF',
        main: secondaryMain,
        dark: '#1939B7',
        contrastText: '#fff',
      },
      info: {
        light: primaryShades.light,
        main: primaryMain,
        dark: primaryShades.dark,
        contrastText: '#fff',
      },
      success: {
        light: '#AAF27F',
        main: '#54D62C',
        dark: '#229A16',
        contrastText: GREY[800],
      },
      warning: {
        light: '#FFE16A',
        main: '#FFC107',
        dark: '#B78103',
        contrastText: GREY[800],
      },
      error: {
        light: '#FF0A0A',
        main: '#FF0A0A',
        dark: '#FF0A0A',
        contrastText: '#fff',
      },
      grey: GREY,
      divider: alpha(GREY[500], 0.24),
      text: {
        primary: GREY[800],
        secondary: GREY[600],
        disabled: GREY[500],
      },
      background: {
        paper: '#fff',
        default: backgroundDefault,
      },
      action: {
        active: GREY[600],
        hover: alpha(GREY[500], 0.08),
        selected: alpha(GREY[500], 0.16),
        disabled: alpha(GREY[500], 0.8),
        disabledBackground: alpha(GREY[500], 0.24),
        focus: alpha(GREY[500], 0.24),
        hoverOpacity: 0.08,
        disabledOpacity: 0.48,
      },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: FONT_PRIMARY,
      fontWeightRegular: 400,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: {
        fontWeight: 700,
        lineHeight: 80 / 64,
        fontSize: pxToRem(40),
        ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
      },
      h2: {
        fontWeight: 700,
        lineHeight: 64 / 48,
        fontSize: pxToRem(32),
        ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
      },
      h3: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(24),
        ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 }),
      },
      h4: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(20),
        ...responsiveFontSizes({ sm: 20, md: 24, lg: 24 }),
      },
      h5: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(18),
        ...responsiveFontSizes({ sm: 19, md: 20, lg: 20 }),
      },
      h6: {
        fontWeight: 700,
        lineHeight: 28 / 18,
        fontSize: pxToRem(17),
        ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
      },
      subtitle1: {
        fontWeight: 600,
        lineHeight: 1.5,
        fontSize: pxToRem(16),
      },
      subtitle2: {
        fontWeight: 600,
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
      },
      body1: {
        lineHeight: 1.5,
        fontSize: pxToRem(16),
      },
      body2: {
        lineHeight: 22 / 14,
        fontSize: pxToRem(14),
      },
      caption: {
        lineHeight: 1.5,
        fontSize: pxToRem(12),
      },
      overline: {
        fontWeight: 700,
        lineHeight: 1.5,
        fontSize: pxToRem(12),
        letterSpacing: 1.1,
        textTransform: 'uppercase',
      },
      button: {
        fontWeight: 700,
        lineHeight: 24 / 14,
        fontSize: pxToRem(14),
        textTransform: 'none',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            boxSizing: 'border-box',
          },
          html: {
            width: '100%',
            height: '100%',
            WebkitOverflowScrolling: 'touch',
          },
          body: {
            width: '100%',
            height: '100%',
            fontFamily: FONT_PRIMARY,
          },
          '#root': {
            width: '100%',
            minHeight: '100%',
          },
          img: {
            display: 'block',
            maxWidth: '100%',
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxShadow: theme.shadows[2],
            borderRadius: Number(theme.shape.borderRadius) * 2,
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': {
              boxShadow: 'none',
            },
          },
          sizeLarge: {
            height: 48,
          },
          containedPrimary: ({ theme }) => ({
            background: DASHBOARD_BRAND_GRADIENT,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: 'none',
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.grey[500], 0.32),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            backgroundColor: alpha(theme.palette.grey[500], 0.12),
            '&:hover': {
              backgroundColor: alpha(theme.palette.grey[500], 0.16),
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.action.focus,
            },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: Number(theme.shape.borderRadius) * 2,
            marginInline: theme.spacing(1),
            marginBlock: theme.spacing(0.25),
            color: theme.palette.text.secondary,
            '&.Mui-selected': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.action.selected,
              '&:hover': {
                backgroundColor: theme.palette.action.selected,
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            minWidth: 40,
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'transparent',
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },
    },
  };

  return createTheme(options);
}
